import { FC, useEffect, useState } from 'react';
import { Range } from 'react-range';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { useTranslation } from 'react-i18next';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { FilterModel } from 'ts/hotel/classes/FilterModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBill1 } from '@fortawesome/free-solid-svg-icons';

interface FilterResultPriceProps {
  filter: FilterModel;
  onFilterChange: (filter: FilterModel) => void;
}

const FilterResultPrice2: FC<FilterResultPriceProps> = (props) => {
  const [values, setValues] = useState(props.filter.price.value);
  const [minInput, setMinInput] = useState(String(props.filter.price.value[0]));
  const [maxInput, setMaxInput] = useState('');
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const { t } = useTranslation();

  useEffect(() => {
    const { price } = props.filter;
    setValues(price.value);
    setMinInput(String(price.value[0]));
    setMaxInput(price.value[1] === price.max ? '' : String(price.value[1]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filter.price.value, props.filter.price.min, props.filter.price.max]);

  const updateFilter = (newValues: [number, number]) => {
    setValues(newValues);
    props.onFilterChange({
      ...props.filter,
      price: { ...props.filter.price, value: newValues },
    });
  };

  const onSliderChange = (newValues: number[]) => {
    const [min, max] = newValues as [number, number];
    setValues([min, max]);
    setMinInput(String(min));

    if (max !== props.filter.price.max) {
      setMaxInput(String(max));
    }
  };

  const onSliderFinalChange = (newValues: number[]) => {
    updateFilter(newValues as [number, number]);
  };

  const sanitizeNumberInput = (value: string) => {
    let sanitized = value.replace(/[^0-9-]/g, '');
    if (sanitized.includes('-')) {
      const isNegative = sanitized[0] === '-';
      const digits = sanitized.replace(/-/g, '');
      sanitized = (isNegative ? '-' : '') + digits;
    }
    return sanitized;
  };

  const onMinInputChange = (value: string) => {
    const sanitized = sanitizeNumberInput(value);
    setMinInput(sanitized);

    if (sanitized === '' || sanitized === '-') {
      return;
    }

    const numValue = Number(sanitized);
    if (!isNaN(numValue)) {
      const newValues: [number, number] = [numValue, values[1]];
      setValues(newValues);
      updateFilter(newValues);
    }
  };

  const onMaxInputChange = (value: string) => {
    const sanitized = sanitizeNumberInput(value);
    setMaxInput(sanitized);

    if (sanitized === '-') {
      return;
    }

    if (sanitized === '') {
      const newValues: [number, number] = [values[0], props.filter.price.max];
      setValues(newValues);
      updateFilter(newValues);
      return;
    }

    const numValue = Number(sanitized);
    if (!isNaN(numValue)) {
      const newValues: [number, number] = [values[0], numValue];
      setValues(newValues);
      updateFilter(newValues);
    }
  };

  return (
    <div className="prices-filter" data-assistant-target="price">
      <div className="mb-10 ">
        <FontAwesomeIcon icon={faMoneyBill1} className="mr-5" /> {t('filter.by_prices')}
      </div>
      <div className="mb-20 flex-row">
        <FormGroup
          className='flex-one'
          movablePlaceholder={t('filter.min')}
          movablePlaceholderSm={true}
          value={minInput}
          onChange={onMinInputChange}
          label=""
        />
        <FormGroup
          className='flex-one ml-10'
          movablePlaceholder={t('filter.max')}
          movablePlaceholderSm={true}
          value={maxInput}
          onChange={onMaxInputChange}
          label=""
        />
      </div>

      <Range
        step={1}
        min={props.filter.price.min}
        max={props.filter.price.max}
        onFinalChange={onSliderFinalChange}
        rtl={false}
        values={values}
        onChange={onSliderChange}
        renderTrack={({ props, children }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: '4px',
              width: '100%',
              backgroundColor: '#ccc',
            }}
          >
            {children}
          </div>
        )}
        renderThumb={({ props }) => (
          <div
            {...props}
            style={{
              ...props.style,
              height: '16px',
              width: '16px',
              borderRadius: '50%',
              backgroundColor: colorContrast,
            }}
          />
        )}
      />
    </div>
  );
};

export default withErrorBoundary(FilterResultPrice2, 'FilterResultPrice2');
